/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../utils/apiBaseUrl.ts';
import EnhancedProductCard from './EnhancedProductCard.tsx';
import { Product } from '../types/Products.ts'; 
import { useProductsCache } from '../contexts/ProductsCacheContext.tsx';
import { useCollection } from '../contexts/CollectionContext.tsx';

interface SalesData {
  data?: {
    firstname?: string;
    lastname?: string;
    email?: string;
    collectionsArray?: any[];
    salesDataByTag?: any[];
  };
}

interface MyProductsProps {
  salesData?: SalesData;
  firebaseToken?: string;
}

const MyProducts = ({ salesData, firebaseToken }: MyProductsProps) => {

  // USE GLOBAL CONTEXT for everything (including isAdmin)
  const { 
    userCollections: collectionsArray,
    selectedCollectionIndex: selectedTagIndex,
    getCollectionTitle,
    isAdmin // ✅ Get isAdmin from context (comes from custom claims)
  } = useCollection();
  
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Admin mode states
  const [hasUserMadeSelection, setHasUserMadeSelection] = useState<boolean>(!isAdmin);
  
  // Use the cache from context
  const cacheRef = useProductsCache();

  // collectionsArray now contains handles directly
  const collectionHandle = React.useMemo(() => {
    if (collectionsArray.length === 0 || selectedTagIndex >= collectionsArray.length) {
      return '';
    }
    return collectionsArray[selectedTagIndex];
  }, [collectionsArray, selectedTagIndex]);

  // Get the display name (title) for the selected collection
  const collectionDisplayName = React.useMemo(() => {
    if (!collectionHandle) return '';
    return getCollectionTitle(collectionHandle);
  }, [collectionHandle, getCollectionTitle]);

  // Set hasUserMadeSelection when admin and collection is selected
  useEffect(() => {
    if (isAdmin && collectionsArray.length > 0) {
      setHasUserMadeSelection(true);
    }
  }, [isAdmin, collectionsArray]);

  // Fetch products when tag selection changes
  useEffect(() => {
    // Skip fetch if admin hasn't made a selection yet
    if (isAdmin && !hasUserMadeSelection) {
      console.log('Admin mode: waiting for user selection');
      return;
    }

    const fetchProducts = async () => {
      if (collectionsArray.length === 0 || !firebaseToken) {
        console.log('No tags or token available');
        return;
      }

      if (!collectionHandle) {
        console.log('No selected collection handle');
        return;
      }

      // Check cache first (cache by handle)
      if (cacheRef.current.has(collectionHandle)) {
        console.log(`Using cached products for collection handle: ${collectionHandle}`);
        setProducts(cacheRef.current.get(collectionHandle) || []);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log('Fetching products for collection handle:', collectionHandle);
        console.log('Collection display name:', collectionDisplayName);
        console.log('Is Admin (from custom claims via context):', isAdmin);
        
        const requestBody = { 
          collectionHandle: collectionHandle
        };
        
        console.log('Request body:', requestBody);
        
        const response = await fetch(`${API_BASE_URL}/.netlify/functions/get-shopify-products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${firebaseToken}`
          },
          body: JSON.stringify(requestBody)
        });

        // Read as text first so we can log the actual error if the response is not JSON
        const responseText = await response.text();
        let data;
        try {
          data = JSON.parse(responseText);
        } catch {
          console.error('Non-JSON response from get-shopify-products:', responseText.slice(0, 500));
          throw new Error(
            response.status === 504
              ? 'Request timed out — too many products to load. Please try again.'
              : `Server error (${response.status}): unexpected response format`
          );
        }

        if (!response.ok) {
          if (!firebaseToken) {
            alert(`Please refresh the page or log back in to see results`);
            return;
          }
          throw new Error(data?.error || data?.message || `Failed to fetch products (${response.status})`);
        }

        console.log('Products received:', data);
        
        const fetchedProducts = data.products || [];
        
        // Cache products by handle
        cacheRef.current.set(collectionHandle, fetchedProducts);
        console.log(`Cached ${fetchedProducts.length} products for handle: ${collectionHandle}`);
        
        setProducts(fetchedProducts);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err instanceof Error ? err.message : 'Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [selectedTagIndex, collectionsArray, collectionHandle, collectionDisplayName, firebaseToken, isAdmin, hasUserMadeSelection, cacheRef]);

  // Show admin mode indicator
  const renderAdminIndicator = () => {
    if (!isAdmin) return null;
    
    return (
      <div style={{
        background: '#d4edda', 
        color: '#155724', 
        padding: '10px', 
        borderRadius: '6px', 
        marginBottom: '20px',
        fontSize: '14px'
      }}>
        <strong>Admin Mode:</strong> Viewing products for selected collection
        {cacheRef.current.size > 0 && (
          <span style={{ marginLeft: '10px' }}>
            ({cacheRef.current.size} collections cached)
          </span>
        )}
      </div>
    );
  };

  // Show message if no collections available
  if (collectionsArray.length === 0) {
    return (
      <div className="page-container">
        <h1>My Products</h1>
        <div className="content-card" style={{ padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
          <p>{isAdmin ? 'No collections available. Try logging out and back in.' : 'No product collections assigned to your account.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1>My Products</h1>
      
      {renderAdminIndicator()}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ fontSize: '18px', color: '#666' }}>Loading products...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#fee', 
          borderRadius: '6px',
          border: '1px solid #fcc',
          marginBottom: '20px'
        }}>
          <p style={{ color: '#c33', margin: 0 }}>Error: {error}</p>
          <button
            onClick={() => {
              // Clear cache for this collection and retry
              cacheRef.current.delete(collectionHandle);
              setError(null);
              // Force re-fetch by toggling hasUserMadeSelection
              setHasUserMadeSelection(false);
              setTimeout(() => setHasUserMadeSelection(true), 0);
            }}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Products Grid - Using EnhancedProductCard */}
      {!loading && !error && products.length > 0 && (
        <div>
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontSize: '16px', color: '#666' }}>
              Found {products.length} product{products.length !== 1 ? 's' : ''} for{' '}
              <strong>{collectionDisplayName}</strong>
            </p>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {products.map((product) => (
              <EnhancedProductCard
                key={product.id}
                product={product}
                firebaseToken={firebaseToken}
                isAdmin={isAdmin}
                collectionHandle={collectionHandle}
              />
            ))}
          </div>
        </div>
      )}

      {/* No Products Found */}
      {!loading && !error && products.length === 0 && hasUserMadeSelection && (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px'
        }}>
          <p style={{ fontSize: '18px', color: '#666', margin: 0 }}>
            No products found for this collection.
          </p>
        </div>
      )}
    </div>
  );
};

export default MyProducts;
